const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:5001/api";

const getAdminToken = () => {
  try {
    return window.sessionStorage.getItem("shawarmaDoseAdminToken") || "";
  } catch (_) {
    return "";
  }
};

async function request(path, options = {}) {
  let response;

  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        ...(options.headers || {}),
      },
    });
  } catch (error) {
    if (error?.name === "AbortError") throw error;
    throw new Error("Cannot reach the API. Check server URL or CORS settings.");
  }

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data.message || "Request failed");
  }

  return data;
}

export const adminLogin = (email, password) => {
  return request("/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
};

export const fetchOffers = () => {
  return request("/offers");
};

export const upsertOffer = (contentfulItemId, discountPercent) => {
  return request(`/offers/${contentfulItemId}`, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${getAdminToken()}`,
    },
    body: JSON.stringify({ enabled: true, discountPercent }),
  });
};

export const deleteOffer = (contentfulItemId) => {
  return request(`/offers/${contentfulItemId}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${getAdminToken()}`,
    },
  });
};

export const applyOffersBatch = (contentfulItemIds, discountPercent) => {
  return request("/offers/batch", {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${getAdminToken()}`,
    },
    body: JSON.stringify({ contentfulItemIds, enabled: true, discountPercent }),
  });
};

export const removeOffersBatch = (contentfulItemIds) => {
  return request("/offers/batch", {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${getAdminToken()}`,
    },
    body: JSON.stringify({ contentfulItemIds, enabled: false, discountPercent: 0 }),
  });
};

export const createNgeniusCheckout = (order) => {
  return request("/payments/checkout", {
    method: "POST",
    body: JSON.stringify(order),
  });
};

export const fetchOrderQuote = (items, signal) => {
  return request("/payments/quote", {
    method: "POST",
    body: JSON.stringify({ items }),
    signal,
  });
};

export const submitWhatsAppOrder = (order) => {
  return request("/payments/whatsapp-order", {
    method: "POST",
    body: JSON.stringify(order),
  });
};

export const reverseGeocodeLocation = (latitude, longitude, language, signal) => {
  const params = new URLSearchParams({
    lat: String(latitude),
    lng: String(longitude),
    lang: String(language || "en"),
  });
  return request(`/location/reverse?${params.toString()}`, { signal });
};

export const searchAddress = (query, language, signal) => {
  const params = new URLSearchParams({
    q: String(query || ""),
    lang: String(language || "en"),
  });
  return request(`/location/search?${params.toString()}`, { signal });
};

const paymentStatusRequests = new Map();

export const verifyNgeniusPayment = (orderRef) => {
  if (paymentStatusRequests.has(orderRef)) {
    return paymentStatusRequests.get(orderRef);
  }

  const pending = request("/payments/status", {
    method: "POST",
    body: JSON.stringify({ orderRef }),
  }).finally(() => {
    window.setTimeout(() => paymentStatusRequests.delete(orderRef), 1000);
  });
  paymentStatusRequests.set(orderRef, pending);
  return pending;
};

export const fetchPaymentOrders = () => {
  return request("/payments/orders", {
    headers: { Authorization: `Bearer ${getAdminToken()}` },
  });
};

export const fetchMarketingCustomers = () => {
  return request("/payments/marketing-customers", {
    headers: { Authorization: `Bearer ${getAdminToken()}` },
  });
};

const getKitchenPin = () => {
  try {
    return window.localStorage.getItem("shawarmaDoseKitchenPin") || "";
  } catch (_) {
    return "";
  }
};

export const saveKitchenPin = (pin) => {
  try {
    window.localStorage.setItem("shawarmaDoseKitchenPin", pin);
  } catch (_) {}
};

export const clearKitchenPin = () => {
  try {
    window.localStorage.removeItem("shawarmaDoseKitchenPin");
  } catch (_) {}
};

export const fetchKitchenOrders = () => {
  return request("/payments/kitchen/orders", {
    headers: { "x-kitchen-pin": getKitchenPin() },
  });
};

export const markKitchenOrderSeen = (orderRef) => {
  return request(`/payments/kitchen/orders/${encodeURIComponent(orderRef)}/seen`, {
    method: "POST",
    headers: { "x-kitchen-pin": getKitchenPin() },
  });
};

export const acknowledgeKitchenOrder = (orderRef) => {
  return request(`/payments/kitchen/orders/${encodeURIComponent(orderRef)}/ack`, {
    method: "POST",
    headers: { "x-kitchen-pin": getKitchenPin() },
  });
};

export const fetchRecentDoneKitchenOrders = () => {
  return request("/payments/kitchen/orders/done", {
    headers: { "x-kitchen-pin": getKitchenPin() },
  });
};

export const unacknowledgeKitchenOrder = (orderRef) => {
  return request(`/payments/kitchen/orders/${encodeURIComponent(orderRef)}/unack`, {
    method: "POST",
    headers: { "x-kitchen-pin": getKitchenPin() },
  });
};

export const saveAdminToken = (token) => {
  try {
    window.sessionStorage.setItem("shawarmaDoseAdminToken", token);
  } catch (_) {}
};

export const clearAdminToken = () => {
  try {
    window.sessionStorage.removeItem("shawarmaDoseAdminToken");
  } catch (_) {}
};
